import {
  FieldWrapper,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import produce from 'immer';
import {
  CreateSavedSearchApiArg,
  SearchFilterField,
  SearchParameters,
} from 'lib-search-types';
import { isLeafFilterCondition } from 'lib-search-ui';
import { HookFormText } from 'lib-shared-hook-form';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import omit from 'lodash/omit';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { searchApi } from 'apis/search';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { sanitizeHtml } from 'utils/sanitizeHtml';

import { searchActions } from '../../../search.actions';
import { SavedSearchFilters } from '../filters/savedSearchFilters';

import styles from './savedSearchFormModal.module.scss';

const formId = 'saved-search-form';

type SavedSearchFormValues = CreateSavedSearchApiArg<'conversations'>;

export const SavedSearchFormModal = createAppModal(
  { type: 'search:savedSearch:form' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const notify = useNotifications();

    const { params, savedSearch: activeSavedSearch } = useAppSelector(
      (state) => state.search,
    );

    const { mode = 'create' } = modal.arg;

    const [createSavedSearch, createState] =
      searchApi.useCreateSavedSearchMutation();
    const [updateSavedSearch, updateState] =
      searchApi.useUpdateSavedSearchMutation();

    const form = useForm<SavedSearchFormValues>({
      mode: 'all',
      defaultValues: {
        name: (mode === 'update' && activeSavedSearch?.name) || '',
        searchRequest: {
          ...activeSavedSearch?.searchRequest,
          ...omit(params, 'page', 'pageSize'),
        } satisfies SearchParameters<'conversations'>,
      },
    });

    const searchRequestValue = useWatch({
      control: form.control,
      name: 'searchRequest',
    });

    const handleCreateSavedSearch = async (values: SavedSearchFormValues) => {
      try {
        const {
          data: { savedSearch },
        } = await createSavedSearch(values).unwrap();

        ampli.savedSearchCreated({
          saveAsNew: !!activeSavedSearch,
        });

        notify(
          {
            title: 'Saved search created',
            text: sanitizeHtml(
              `"${savedSearch.name}" has been created.`,
              'plainText',
            ),
            className: 'success',
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        return savedSearch;
      } catch (error) {
        notify(
          {
            title: 'An error occurred',
            text: `An error occurred when trying to create the saved search.`,
          },
          NotificationType.Error,
        );
      }
    };

    const handleUpdateSavedSearch = async (values: SavedSearchFormValues) => {
      if (!activeSavedSearch) return;

      try {
        const {
          data: { savedSearch },
        } = await updateSavedSearch({
          ...activeSavedSearch,
          ...values,
        }).unwrap();

        ampli.savedSearchUpdated();

        notify(
          {
            title: 'Saved search update',
            text: sanitizeHtml(
              `"${savedSearch.name}" has been updated.`,
              'plainText',
            ),
            className: 'success',
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        return savedSearch;
      } catch (error) {
        notify(
          {
            title: 'An error occurred',
            text: `An error occurred when trying to update the saved search.`,
          },
          NotificationType.Error,
        );
      }
    };

    const handleValidSubmit: SubmitHandler<SavedSearchFormValues> = async (
      values,
    ) => {
      const savedSearch =
        mode === 'update'
          ? await handleUpdateSavedSearch(values)
          : await handleCreateSavedSearch(values);

      if (savedSearch) {
        dispatch(searchActions.savedSearch.apply(savedSearch));
      }

      hide?.();
    };

    const handleRemove = (
      field: 'query' | SearchFilterField<'conversations'>,
      index: number,
    ) => {
      const nextParams: SavedSearchFormValues['searchRequest'] = produce(
        searchRequestValue,
        (draft) => {
          if (field === 'query') {
            draft.query = '';
            draft.isStrict = false;
          } else {
            const condition = draft.filters?.filters[index];

            if (
              condition &&
              isLeafFilterCondition(condition) &&
              condition.field === field
            ) {
              draft.filters?.filters.splice(index, 1);
            }
          }
        },
      );

      form.setValue('searchRequest', nextParams, { shouldDirty: true });
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead
          title={
            mode === 'update' ? 'Update saved search' : 'Create saved search'
          }
          onClose={hide}
        >
          {mode === 'create' &&
            'Saving a search makes it easy to come back to where you left off. All you have to do is give it a name.'}
        </ModalHead>

        <ModalBody>
          <form
            id={formId}
            noValidate
            tabIndex={-1}
            onSubmit={form.handleSubmit(handleValidSubmit)}
          >
            <FormGrid>
              <FormField>
                <HookFormText
                  name="name"
                  label="Name"
                  control={form.control}
                  rules={{
                    required: 'Name is required',
                  }}
                  componentProps={{
                    autoFocus: true,
                    placeholder: 'e.g. Assigned to me',
                  }}
                />
              </FormField>

              <FormField>
                <FieldWrapper labelFor="filters" label="Filters applied">
                  <SavedSearchFilters
                    variant="chips"
                    params={searchRequestValue}
                    onRemove={handleRemove}
                  />
                </FieldWrapper>
              </FormField>
            </FormGrid>
          </form>
        </ModalBody>

        <ModalFoot
          confirmName={mode === 'update' ? 'Update search' : 'Save search'}
          cancelName="Cancel"
          confirmOnClick={noop}
          cancelOnClick={hide}
          confirmProps={{
            type: 'submit',
            isLoading: createState.isLoading || updateState.isLoading,
            disabled: !form.formState.isValid || form.formState.isSubmitting,
            form: formId,
          }}
        />
      </Modal>
    );
  },
);
